import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Import pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import LeadsPage from "./pages/LeadsPage";
import LeadDetailPage from "./pages/LeadDetailPage";
import TasksPage from "./pages/TasksPage";
import SiteVisitsPage from "./pages/SiteVisitsPage";
import CopilotPage from "./pages/CopilotPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import SettingsPage from "./pages/SettingsPage";
import IntelligencePage from "./pages/IntelligencePage";
import ManagementCopilotPage from "./pages/ManagementCopilotPage";
import LeadCaptureSettingsPage from "./pages/LeadCaptureSettingsPage";
import PartnerFormPage from "./pages/PartnerFormPage";
import WalkInPage from "./pages/WalkInPage";
import CampaignsPage from "./pages/CampaignsPage";
import TeamsPage from "./pages/TeamsPage";
import RoutingRulesPage from "./pages/RoutingRulesPage";
import IntegrationHealthPage from "./pages/IntegrationHealthPage";
import TrainingAdminPage from "./pages/TrainingAdminPage";
import TrainingCatalogPage from "./pages/TrainingCatalogPage";
import RoutingLogsPage from "./pages/RoutingLogsPage";
import EscalationDashboardPage from "./pages/EscalationDashboardPage";
import CampaignAnalyticsPage from "./pages/CampaignAnalyticsPage";
import ChannelPartnersPage from "./pages/ChannelPartnersPage";
import FamilyGroupsPage from "./pages/FamilyGroupsPage";
import WorkflowBuilderPage from "./pages/WorkflowBuilderPage";
import AutomationLibraryPage from "./pages/AutomationLibraryPage";
import MessageTemplatesPage from "./pages/MessageTemplatesPage";
import ProjectComparisonPage from "./pages/ProjectComparisonPage";
import EMICalculatorPage from "./pages/EMICalculatorPage";
import ObjectionHandlerPage from "./pages/ObjectionHandlerPage";
import ControlTowerPage from "./pages/ControlTowerPage";
import ChatbotPage from "./pages/ChatbotPage";
import TelecallerQueuePage from "./pages/TelecallerQueuePage";
import InventoryPage from "./pages/InventoryPage";
import NegotiationPage from "./pages/NegotiationPage";
import DealRoomsListPage from "./pages/DealRoomsListPage";
import DealRoomPage from "./pages/DealRoomPage";
import HelpCenterPage from "./pages/HelpCenterPage";
import ContextualHelpBubble from "./components/ContextualHelpBubble";
import PricingPage from "./pages/PricingPage";
import GovernancePage from "./pages/GovernancePage";
import ManagerDashboardPage from "./pages/ManagerDashboardPage";
import AITrainingPage from "./pages/AITrainingPage";
import LostReasonAnalyticsPage from "./pages/LostReasonAnalyticsPage";
import AuditPage from "./pages/AuditPage";
import DiscountApprovalsPage from "./pages/DiscountApprovalsPage";
import BookingsPage from "./pages/BookingsPage";
import EOIPage from "./pages/EOIPage";
import ReferralsPage from "./pages/ReferralsPage";
import ChannelPartnerPortal from "./pages/ChannelPartnerPortal";
import CPSubmissionsPage from "./pages/CPSubmissionsPage";
import DataHealthPage from "./pages/DataHealthPage";
import DataImportPage from "./pages/DataImportPage";
import FormBuilderPage from "./pages/FormBuilderPage";
import ParkingManagementPage from "./pages/ParkingManagementPage";
import AILearningDashboardPage from "./pages/AILearningDashboardPage";
import QueuesPage from "./pages/QueuesPage";
import SyncDashboardPage from "./pages/SyncDashboardPage";
import PublicToolPage from "./pages/PublicToolPage";
import MhMPage from "./pages/MhMPage";
import MhMCampaignPage from "./pages/MhMCampaignPage";
import MhMInsightsPage from "./pages/MhMInsightsPage";
import MhMReportPage from "./pages/MhMReportPage";
import MhMComparisonReportPage from "./pages/MhMComparisonReportPage";
import MhMNextStepPage from "./pages/MhMNextStepPage";
import MhMProjectComparePage from "./pages/MhMProjectComparePage";
import LeadInboxPage from "./pages/LeadInboxPage";
import ReportsPage from "./pages/ReportsPage";
import RentalDashboardPage from "./pages/rentals/RentalDashboardPage";
import RentalUnitsPage from "./pages/rentals/RentalUnitsPage";
import RentalTenantLeadsPage from "./pages/rentals/RentalTenantLeadsPage";
import RentalVisitsPage from "./pages/rentals/RentalVisitsPage";
import RentalLeasesPage from "./pages/rentals/RentalLeasesPage";
import RentalServiceRequestsPage from "./pages/rentals/RentalServiceRequestsPage";

import "./App.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Use REACT_APP_BACKEND_URL for preview; fall back to same-origin for production deployments
import { BACKEND_URL, API } from "./config/api";

// Configure axios defaults
axios.defaults.withCredentials = true;

// Bearer-token fallback for cross-origin deploys (e.g. crm.rajatbuildtech.com →
// backend on a different host). Modern Chrome blocks third-party cookies even
// with SameSite=None; Secure, so we mirror the session_token in localStorage and
// always send it as `Authorization: Bearer` — backend's get_current_user accepts
// either source. Cookie remains primary for same-origin / older browsers.
const SESSION_TOKEN_KEY = "propertycrm_session_token";
const setStoredSessionToken = (token) => {
  if (token) {
    try { localStorage.setItem(SESSION_TOKEN_KEY, token); } catch {}
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    try { localStorage.removeItem(SESSION_TOKEN_KEY); } catch {}
    delete axios.defaults.headers.common["Authorization"];
  }
};
// Re-hydrate on app boot (so refreshes don't lose auth)
try {
  const _t = localStorage.getItem(SESSION_TOKEN_KEY);
  if (_t) axios.defaults.headers.common["Authorization"] = `Bearer ${_t}`;
} catch {}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Project Context — global project selector
const ProjectContext = createContext(null);

export const useProject = () => useContext(ProjectContext);

const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    const stored = localStorage.getItem("selectedProjectId");
    return stored || "all";
  });

  useEffect(() => {
    axios.get(`${API}/projects`).then(res => {
      const projs = res.data || [];
      setProjects(projs);
      // If stored project doesn't exist anymore, reset to all
      const stored = localStorage.getItem("selectedProjectId");
      if (stored && stored !== "all" && !projs.find(p => p.project_id === stored)) {
        setSelectedProjectId("all");
        localStorage.setItem("selectedProjectId", "all");
      }
    }).catch((err) => {
      console.error("Failed to load projects:", err);
      setProjects([]);
    });
  }, []);

  const selectProject = (projectId) => {
    setSelectedProjectId(projectId);
    localStorage.setItem("selectedProjectId", projectId);
  };

  const selectedProject = projects.find(p => p.project_id === selectedProjectId) || null;
  const projectFilter = selectedProjectId === "all" ? {} : { project_id: selectedProjectId, project_interest: selectedProject?.name || "" };

  return (
    <ProjectContext.Provider value={{ projects, selectedProjectId, selectedProject, projectFilter, selectProject }}>
      {children}
    </ProjectContext.Provider>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { timeout: 10000 });
      setUser(response.data);
      return response.data;
    } catch (error) {
      if (error.response?.status === 403) {
        window.location.href = '/pending';
        return null;
      }
      // 401 / network: stale or missing token — clear to force re-login
      if (error.response?.status === 401) {
        setStoredSessionToken(null);
      }
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    setStoredSessionToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.replace('#', '?')).get('session_id');
      
      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        if (response.data.success) {
          // Store bearer token for cross-origin deploys where cookies are blocked
          if (response.data.session_token) {
            setStoredSessionToken(response.data.session_token);
          }
          if (response.data.status === "pending") {
            navigate('/pending', { replace: true });
            return;
          }
          login(response.data.user);
          toast.success(`Welcome, ${response.data.user.name}!`);
          navigate('/dashboard', { replace: true, state: { user: response.data.user } });
        }
      } catch (error) {
        console.error('Auth error:', error);
        const detail = error.response?.data?.detail || '';
        if (detail.includes('unavailable')) {
          toast.error('Auth service temporarily unavailable. Please try again.');
        } else if (detail.includes('rejected')) {
          toast.error('Your account has been rejected. Contact your admin.');
          navigate('/pending', { replace: true });
          return;
        } else {
          toast.error('Authentication failed. Please try again.');
        }
        navigate('/login');
      }
    };

    processAuth();
  }, [location, navigate, login]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
        <p className="mt-4 text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Pending Approval Page
const PendingPage = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="text-center max-w-md p-8">
      <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      </div>
      <h2 className="text-xl font-bold text-slate-900 mb-2">Account Pending Approval</h2>
      <p className="text-slate-500 text-sm mb-6">Your account has been created but needs approval from an administrator before you can access the CRM. You'll be notified once approved.</p>
      <a href="/login" className="text-sm text-blue-600 hover:underline">Back to Login</a>
    </div>
  </div>
);

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/pending" element={<PendingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
      <Route path="/leads/:leadId" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/site-visits" element={<ProtectedRoute><SiteVisitsPage /></ProtectedRoute>} />
      <Route path="/copilot" element={<ProtectedRoute><CopilotPage /></ProtectedRoute>} />
      <Route path="/management" element={<ProtectedRoute><ManagementCopilotPage /></ProtectedRoute>} />
      <Route path="/intelligence" element={<ProtectedRoute><IntelligencePage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/lead-capture" element={<ProtectedRoute><LeadCaptureSettingsPage /></ProtectedRoute>} />
      <Route path="/walk-in" element={<ProtectedRoute><WalkInPage /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
      <Route path="/teams" element={<ProtectedRoute><TeamsPage /></ProtectedRoute>} />
      <Route path="/routing-rules" element={<ProtectedRoute><RoutingRulesPage /></ProtectedRoute>} />
      <Route path="/integration-health" element={<ProtectedRoute><IntegrationHealthPage /></ProtectedRoute>} />
      <Route path="/training-admin" element={<ProtectedRoute><TrainingAdminPage /></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><TrainingCatalogPage /></ProtectedRoute>} />
      <Route path="/training/course/:courseId" element={<ProtectedRoute><TrainingCatalogPage /></ProtectedRoute>} />
      <Route path="/routing-logs" element={<ProtectedRoute><RoutingLogsPage /></ProtectedRoute>} />
      <Route path="/escalations" element={<ProtectedRoute><EscalationDashboardPage /></ProtectedRoute>} />
      <Route path="/campaign-analytics" element={<ProtectedRoute><CampaignAnalyticsPage /></ProtectedRoute>} />
      <Route path="/channel-partners" element={<ProtectedRoute><ChannelPartnersPage /></ProtectedRoute>} />
      <Route path="/campaign-roi" element={<Navigate to="/campaign-analytics" replace />} />
      <Route path="/source-analytics" element={<Navigate to="/campaign-analytics" replace />} />
      <Route path="/family-groups" element={<ProtectedRoute><FamilyGroupsPage /></ProtectedRoute>} />
      <Route path="/automations" element={<ProtectedRoute><AutomationLibraryPage /></ProtectedRoute>} />
      <Route path="/message-templates" element={<ProtectedRoute><MessageTemplatesPage /></ProtectedRoute>} />
      <Route path="/workflows" element={<ProtectedRoute><WorkflowBuilderPage /></ProtectedRoute>} />
      <Route path="/project-compare" element={<ProtectedRoute><ProjectComparisonPage /></ProtectedRoute>} />
      <Route path="/emi-calculator" element={<ProtectedRoute><EMICalculatorPage /></ProtectedRoute>} />
      <Route path="/objection-handler" element={<ProtectedRoute><ObjectionHandlerPage /></ProtectedRoute>} />
      <Route path="/control-tower" element={<ProtectedRoute><ControlTowerPage /></ProtectedRoute>} />
      <Route path="/chatbot" element={<ProtectedRoute><ChatbotPage /></ProtectedRoute>} />
      <Route path="/telecaller" element={<ProtectedRoute><TelecallerQueuePage /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
      <Route path="/negotiations" element={<ProtectedRoute><NegotiationPage /></ProtectedRoute>} />
      <Route path="/deal-rooms" element={<ProtectedRoute><DealRoomsListPage /></ProtectedRoute>} />
      <Route path="/deal-rooms/:dealRoomId" element={<ProtectedRoute><DealRoomPage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpCenterPage /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
      <Route path="/governance" element={<ProtectedRoute><GovernancePage /></ProtectedRoute>} />
      <Route path="/manager-dashboard" element={<ProtectedRoute><ManagerDashboardPage /></ProtectedRoute>} />
      <Route path="/ai-training" element={<ProtectedRoute><AITrainingPage /></ProtectedRoute>} />
      <Route path="/lost-analytics" element={<ProtectedRoute><LostReasonAnalyticsPage /></ProtectedRoute>} />
      <Route path="/audit" element={<ProtectedRoute><AuditPage /></ProtectedRoute>} />
      <Route path="/discount-approvals" element={<ProtectedRoute><DiscountApprovalsPage /></ProtectedRoute>} />
      <Route path="/bookings" element={<ProtectedRoute><BookingsPage /></ProtectedRoute>} />
      <Route path="/eoi" element={<ProtectedRoute><EOIPage /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} />
      <Route path="/partner-form" element={<PartnerFormPage />} />
      <Route path="/cp/:partnerCode" element={<ChannelPartnerPortal />} />
      <Route path="/cp-submissions" element={<ProtectedRoute><CPSubmissionsPage /></ProtectedRoute>} />
      <Route path="/data-health" element={<ProtectedRoute><DataHealthPage /></ProtectedRoute>} />
      <Route path="/data-import" element={<ProtectedRoute><DataImportPage /></ProtectedRoute>} />
      <Route path="/form-builder" element={<ProtectedRoute><FormBuilderPage /></ProtectedRoute>} />
      <Route path="/parking" element={<ProtectedRoute><ParkingManagementPage /></ProtectedRoute>} />
      <Route path="/ai-learning" element={<ProtectedRoute><AILearningDashboardPage /></ProtectedRoute>} />
      <Route path="/queues" element={<ProtectedRoute><QueuesPage /></ProtectedRoute>} />
      <Route path="/sync-dashboard" element={<ProtectedRoute><SyncDashboardPage /></ProtectedRoute>} />
      <Route path="/mhm" element={<ProtectedRoute><MhMPage /></ProtectedRoute>} />
      <Route path="/mhm/start" element={<MhMCampaignPage />} />
      <Route path="/mhm/report/:sessionId" element={<MhMReportPage />} />
      <Route path="/mhm/comparison-report/:sessionId" element={<MhMComparisonReportPage />} />
      <Route path="/mhm/next/:sessionId" element={<MhMNextStepPage />} />
      <Route path="/mhm/projects/:sessionId" element={<MhMProjectComparePage />} />
      <Route path="/mhm-insights" element={<ProtectedRoute><MhMInsightsPage /></ProtectedRoute>} />
      <Route path="/lead-inbox" element={<ProtectedRoute><LeadInboxPage /></ProtectedRoute>} />
      <Route path="/rentals" element={<ProtectedRoute><RentalDashboardPage /></ProtectedRoute>} />
      <Route path="/rentals/units" element={<ProtectedRoute><RentalUnitsPage /></ProtectedRoute>} />
      <Route path="/rentals/tenant-leads" element={<ProtectedRoute><RentalTenantLeadsPage /></ProtectedRoute>} />
      <Route path="/rentals/visits" element={<ProtectedRoute><RentalVisitsPage /></ProtectedRoute>} />
      <Route path="/rentals/leases" element={<ProtectedRoute><RentalLeasesPage /></ProtectedRoute>} />
      <Route path="/rentals/service-requests" element={<ProtectedRoute><RentalServiceRequestsPage /></ProtectedRoute>} />
      <Route path="/tools/:token" element={<PublicToolPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProjectProvider>
          <AppRouter />
          <ContextualHelpBubble />
          <Toaster position="top-right" richColors />
        </ProjectProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
